import { supabase } from '@/integrations/supabase/client';

export interface TimeEntry {
  id: string;
  task_id: string;
  user_id: string;
  duration_seconds: number;
  logged_at: string;
  session_type: 'pomodoro' | 'break';
  session_status: 'in_progress' | 'completed' | 'interrupted';
  created_at: string;
}

export const createTimeEntry = async (
  taskId: string,
  userId: string,
  durationSeconds: number,
  sessionType: 'pomodoro' | 'break' = 'pomodoro',
  sessionStatus: 'in_progress' | 'completed' | 'interrupted' = 'completed'
): Promise<string | null> => {
  const { data, error } = await supabase
    .from('time_entries')
    .insert({
      task_id: taskId,
      user_id: userId,
      duration_seconds: durationSeconds,
      session_type: sessionType,
      session_status: sessionStatus,
      logged_at: new Date().toISOString()
    })
    .select('id')
    .single();

  if (error) {
    console.error('Error creating time entry:', error);
    throw error;
  }

  return data?.id || null;
};

export const updateTimeEntry = async (
  entryId: string,
  durationSeconds: number,
  sessionStatus: 'in_progress' | 'completed' | 'interrupted' = 'in_progress'
): Promise<void> => {
  const { error } = await supabase
    .from('time_entries')
    .update({
      duration_seconds: durationSeconds,
      session_status: sessionStatus
    })
    .eq('id', entryId);

  if (error) {
    console.error('Error updating time entry:', error);
    throw error;
  }
};

export const finalizeTimeEntry = async (
  entryId: string,
  finalDurationSeconds: number,
  sessionStatus: 'completed' | 'interrupted' = 'completed'
): Promise<void> => {
  const { error } = await supabase
    .from('time_entries')
    .update({
      duration_seconds: finalDurationSeconds,
      session_status: sessionStatus
    })
    .eq('id', entryId);

  if (error) {
    console.error('Error finalizing time entry:', error);
    throw error;
  }
};

export const getActiveTimeEntry = async (
  userId: string,
  taskId: string
): Promise<TimeEntry | null> => {
  const { data, error } = await supabase
    .from('time_entries')
    .select('*')
    .eq('user_id', userId)
    .eq('task_id', taskId)
    .eq('session_status', 'in_progress')
    .eq('session_type', 'pomodoro')
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
    console.error('Error fetching active time entry:', error);
    throw error;
  }

  return data as TimeEntry || null;
};

export const cleanupInProgressEntries = async (
  userId: string,
  taskId: string
): Promise<void> => {
  const { error } = await supabase
    .from('time_entries')
    .update({ session_status: 'interrupted' })
    .eq('user_id', userId)
    .eq('task_id', taskId)
    .eq('session_status', 'in_progress')
    .eq('session_type', 'pomodoro');

  if (error) {
    console.error('Error cleaning up in-progress entries:', error);
    throw error;
  }
};

export const fetchTimeEntriesForUser = async (userId: string): Promise<TimeEntry[]> => {
  const { data, error } = await supabase
    .from('time_entries')
    .select('*')
    .eq('user_id', userId)
    .order('logged_at', { ascending: false });

  if (error) {
    console.error('Error fetching time entries:', error);
    throw error;
  }

  return (data || []) as TimeEntry[];
};

export const fetchTimeEntriesForDateRange = async (
  userId: string,
  startDate: string,
  endDate: string
): Promise<TimeEntry[]> => {
  const { data, error } = await supabase
    .from('time_entries')
    .select('*')
    .eq('user_id', userId)
    .eq('session_type', 'pomodoro')
    .in('session_status', ['completed', 'interrupted']) // Only count finalized sessions for progress
    .gte('logged_at', startDate)
    .lte('logged_at', endDate)
    .order('logged_at', { ascending: true });

  if (error) {
    console.error('Error fetching time entries for date range:', error);
    throw error;
  }

  return (data || []) as TimeEntry[];
};
